/**
 * Light or dark panel, per browser. "auto" follows the device. The sign-in screens are
 * always the dark glass; this only changes the panel.
 */
export type Theme = "light" | "dark" | "auto";

const KEY = "orbis.theme";
const media = () => window.matchMedia("(prefers-color-scheme: dark)");

export function savedTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === "dark" || v === "auto" ? v : "light";
  } catch {
    return "light";
  }
}

const resolve = (t: Theme) => (t === "auto" ? (media().matches ? "dark" : "light") : t);

function apply(t: Theme) {
  const resolved = resolve(t);
  document.documentElement.dataset.theme = resolved;
  document.querySelector('meta[name="color-scheme"]')?.setAttribute("content", resolved);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", resolved === "dark" ? "#081226" : "#FFFFFF");
}

const listeners = new Set<(t: Theme) => void>();

/** Applies the saved theme and keeps "auto" in step with the device. Call once at start. */
export function initTheme() {
  apply(savedTheme());
  media().addEventListener("change", () => {
    if (savedTheme() === "auto") apply("auto");
  });
}

export function setTheme(t: Theme) {
  try {
    localStorage.setItem(KEY, t);
  } catch {
    // Storage blocked: the choice lasts until reload.
  }
  apply(t);
  for (const l of listeners) l(t);
}

export function onThemeChange(fn: (t: Theme) => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** The theme in use right now (auto resolved). */
export const currentTheme = () => (document.documentElement.dataset.theme === "dark" ? "dark" : "light");
